import { esc, kr, nf1, num, pct, qty } from '../calc/format.js';
import { change } from '../calc/periods.js';
import { NO_COST_TITLE, el } from './dom.js';
import { state } from './state.js';

/* ---- KPI ---- */

export const KPI_IDS = ['kAov','kIpt','kRev','kProfit','kMargin','kTx',
                 'kUnitPrice','kMulti','kReturn','kDiscount','kProfitTx','kPerDay'];

/**
 * Bygger undertexten för ett KPI: förändring mot föregående period när den
 * finns, annars den beskrivande texten. Enhetslösa mått som marginal och
 * andelar jämförs i procentenheter, inte i procentuell förändring.
 */
export function delta(nowVal, key, fallback, opts){
  const p = state.prev;
  if(!p) return esc(fallback);

  const before = p.stats[key];
  const isPoints = opts && opts.points;

  let text, dir;
  if(isPoints){
    if(!isFinite(before) || (before === 0 && nowVal === 0)) return esc(fallback);
    const d = nowVal - before;
    if(Math.abs(d) < 0.05) return esc(`oförändrat mot ${p.label}`);
    dir  = d > 0 ? 'up' : 'down';
    text = `${d > 0 ? '+' : '−'}${nf1.format(Math.abs(d)).replace('.',',')} p.e. mot ${p.label}`;
  }else{
    const c = change(nowVal, before);
    if(c === null) return esc(fallback);
    if(Math.abs(c) < 0.5) return esc(`oförändrat mot ${p.label}`);
    dir  = c > 0 ? 'up' : 'down';
    text = `${c > 0 ? '+' : '−'}${nf1.format(Math.abs(c)).replace('.',',')} % mot ${p.label}`;
  }

  // För returer och rabatter är en ökning något negativt. Pilen ska peka åt
  // samma håll som förändringen, men färgen ska spegla om det är bra eller dåligt.
  const good = (opts && opts.invert) ? dir === 'down' : dir === 'up';
  return `<span class="delta ${dir} ${good ? 'good' : 'bad'}">${esc(text)}</span>`;
}

export function renderKpis(s, has){
  if(!has){
    KPI_IDS.forEach(i => el(i).textContent = '–');
    KPI_IDS.forEach(i => el(i+'M').textContent = 'Inga kvitton i urvalet');
    return;
  }

  el('kAov').textContent  = kr(s.aov);
  el('kAovM').innerHTML   = delta(s.aov, 'aov', `Omsättning delat på ${num(s.tx)} köp`);

  el('kIpt').textContent  = nf1.format(s.itemsPerTx).replace('.',',');
  el('kIptM').innerHTML   = delta(s.itemsPerTx, 'itemsPerTx', `${qty(s.units)} artiklar totalt`);

  el('kRev').textContent  = kr(s.revenue);
  el('kRevM').innerHTML   = delta(s.revenue, 'revenue', `${kr(s.revenueEx)} exklusive moms`);

  if(!state.hasCost){
    // Utan inköpspris vore vinsten bara omsättningen exklusive moms
    for(const id of ['kProfit','kMargin','kProfitTx']){
      el(id).textContent = '–';
      el(id + 'M').innerHTML = `<span class="delta warn">${esc(NO_COST_TITLE)}</span>`;
    }
  }else{
    el('kProfit').textContent = kr(s.profit);
    el('kProfitM').innerHTML  = delta(s.profit, 'profit', `${kr(s.revenuePerDay)} per dag i snitt`);

    el('kMargin').textContent = pct(s.margin);
    el('kMarginM').innerHTML  = s.noCostShare >= 5
      ? `<span class="delta warn">${esc(pct(s.noCostShare))} av vinsten saknar inköpspris</span>`
      : delta(s.margin, 'margin', 'Bruttovinst delat på omsättning exkl. moms', {points:true});

    el('kProfitTx').textContent = kr(s.profitPerTx);
    el('kProfitTxM').innerHTML  = delta(s.profitPerTx, 'profitPerTx', 'Bruttovinst delat på antal köp');
  }

  el('kTx').textContent   = num(s.tx);
  el('kTxM').innerHTML    = delta(s.tx, 'tx', s.days === 1 ? 'Under 1 dag' : `Fördelat på ${num(s.days)} dagar`);

  el('kUnitPrice').textContent = kr(s.avgUnitPrice);
  el('kUnitPriceM').innerHTML  = delta(s.avgUnitPrice, 'avgUnitPrice', 'Omsättning delat på antal artiklar');

  el('kMulti').textContent = pct(s.multiLineShare);
  el('kMultiM').innerHTML  = delta(s.multiLineShare, 'multiLineShare',
    `${num(s.multiLineTx)} av ${num(s.tx)} köp`, {points:true});

  el('kReturn').textContent = pct(s.returnShare);
  el('kReturnM').innerHTML  = s.returnValue
    ? delta(s.returnShare, 'returnShare', `${kr(Math.abs(s.returnValue))} på ${num(s.returnReceipts)} kvitton`, {points:true, invert:true})
    : 'Inga returer i perioden';

  el('kDiscount').textContent = pct(s.discountShare);
  el('kDiscountM').innerHTML  = s.discountTotal
    ? delta(s.discountShare, 'discountShare', `${kr(s.discountTotal)} i lämnad rabatt`, {points:true, invert:true})
    : 'Ingen rabatt lämnad';

  el('kPerDay').textContent = kr(s.revenuePerDay);
  el('kPerDayM').innerHTML  = delta(s.revenuePerDay, 'revenuePerDay',
    `${nf1.format(s.txPerDay).replace('.',',')} köp per dag`);
}
